import React, { useState } from 'react';
import { api } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, X } from 'lucide-react';

const LINE_DOT = { day_program: 'bg-[#FFD66B]', in_home: 'bg-[#7EA9F5]', employment: 'bg-[#34C98E]' };

export const WeekMirror = ({ data, reload }) => {
  const { staff, week_dates, line_labels } = data;
  const [sel, setSel] = useState(null); // {shift, staffName}
  const [target, setTarget] = useState('');
  const [saving, setSaving] = useState(false);

  const reassign = async (confirm = false) => {
    if (!target) return;
    setSaving(true);
    try {
      await api.put(`/shifts/${sel.shift.id}?confirm_conflict=${confirm}`, { employee_id: target });
      toast.success('Shift reassigned — live schedule updated');
      setSel(null); setTarget('');
      reload();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det?.code === 'shift_conflict' && !confirm) {
        if (window.confirm(`${det.message}\n\nAssign anyway?`)) { setSaving(false); return reassign(true); }
      } else {
        toast.error(typeof det === 'string' ? det : det?.message || 'Reassign failed');
      }
    }
    setSaving(false);
  };

  return (
    <div className="space-y-3" data-testid="week-mirror">
      <p className="text-xs text-white/40">Live mirror of the working schedule. Tap any shift to reassign it — writes straight back to the real schedule.</p>
      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full text-xs" style={{ minWidth: 900 }}>
          <thead>
            <tr className="bg-white/[0.05]">
              <th className="text-left px-3 py-2 font-bold text-white/60 sticky left-0 bg-[#17141F]">Staff</th>
              {week_dates.map(d => (
                <th key={d} className="px-2 py-2 font-bold text-white/60 text-center">
                  {new Date(d + 'T12:00:00').toLocaleDateString([], { weekday: 'short' })}<br />
                  <span className="text-white/30 font-normal">{new Date(d + 'T12:00:00').toLocaleDateString([], { month: 'numeric', day: 'numeric' })}</span>
                </th>
              ))}
              <th className="px-2 py-2 font-bold text-white/60 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {staff.map(s => (
              <tr key={s.id} className="border-t border-white/5">
                <td className="px-3 py-2 font-semibold sticky left-0 bg-[#12101A] whitespace-nowrap">{s.name}</td>
                {week_dates.map(d => {
                  const rows = s.days[d] || [];
                  const unavailable = s.availability && s.availability[['sun','mon','tue','wed','thu','fri','sat'][new Date(d + 'T12:00:00').getDay()]] === false;
                  const timeOff = s.time_off && s.time_off[d];
                  return (
                    <td key={d} className={`px-1 py-1.5 align-top ${unavailable || timeOff ? 'bg-white/[0.02]' : ''}`}>
                      <div className="space-y-1">
                        {timeOff && (
                          <span className="block text-center text-[9px] font-black uppercase tracking-wider text-[#FFD66B] bg-[#3A2F10]/60 border border-[#FFD66B]/30 rounded-md py-0.5"
                                data-testid={`mirror-timeoff-${s.id}-${d}`}>{timeOff}</span>
                        )}
                        {s.on_call_dates && s.on_call_dates[d] && (
                          <span className="block text-center text-[9px] font-black uppercase tracking-wider text-[#8FD3FF] bg-[#0F2A3A]/80 border border-[#8FD3FF]/30 rounded-md py-0.5"
                                data-testid={`mirror-oncall-${s.id}-${d}`}>📟 on call</span>
                        )}
                        {rows.map(r => (
                          <button key={r.id} onClick={() => { setSel({ shift: r, staffId: s.id, staffName: s.name, date: d }); setTarget(''); }}
                                  className={`w-full text-left rounded-md px-1.5 py-1 border transition-colors ${r.has_conflict ? 'border-[#FF5C7A]/60 bg-[#3A1420]/50' : r.is_draft ? 'border-dashed border-white/25 bg-white/[0.04]' : 'border-white/10 bg-white/[0.07] hover:bg-white/[0.14]'}`}
                                  data-testid={`mirror-shift-${r.id}`}>
                            <span className="flex items-center gap-1">
                              <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${LINE_DOT[r.service_line] || 'bg-white/40'}`} />
                              <span className="font-semibold tabular-nums">{r.time_in}–{r.time_out}</span>
                              {r.is_draft && <span className="text-[8px] uppercase text-white/40">draft</span>}
                            </span>
                            {r.participants.length > 0 && <span className="block text-[9px] text-white/40 truncate">{r.participants.join(', ')}</span>}
                          </button>
                        ))}
                        {rows.length === 0 && <span className="block text-center text-white/15">{unavailable ? '—' : ''}</span>}
                      </div>
                    </td>
                  );
                })}
                <td className="px-2 py-2 text-right font-bold tabular-nums">{s.hours_total}h</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-[10px] text-white/40">
        {Object.entries(line_labels).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${LINE_DOT[k]}`} /> {v}</span>
        ))}
        <span className="flex items-center gap-1"><span className="h-2 w-4 rounded border border-dashed border-white/40" /> Draft</span>
        <span className="flex items-center gap-1"><span className="h-2 w-4 rounded border border-[#FF5C7A]" /> Conflict</span>
      </div>

      {/* Reassign panel */}
      {sel && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={() => setSel(null)}>
          <div className="bg-[#17141F] border border-white/10 rounded-2xl p-5 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()} data-testid="reassign-panel">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-bold">Reassign shift</h3>
                <p className="text-xs text-white/40 mt-0.5">
                  {sel.staffName} · {new Date(sel.date + 'T12:00:00').toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })} · {sel.shift.time_in}–{sel.shift.time_out} · {line_labels[sel.shift.service_line]}
                </p>
              </div>
              <button onClick={() => setSel(null)} className="text-white/40 hover:text-white" data-testid="reassign-close"><X className="h-4 w-4" /></button>
            </div>
            <select value={target} onChange={e => setTarget(e.target.value)}
                    className="w-full h-11 rounded-xl bg-white/5 border border-white/15 px-3 text-sm font-semibold"
                    data-testid="reassign-select">
              <option value="">Choose new staff…</option>
              {staff.filter(x => x.id !== sel.staffId && x.service_lines.includes(sel.shift.service_line)).map(x => (
                <option key={x.id} value={x.id} className="text-black">
                  {`${x.name} — ${x.hours_total}h/${x.target}h${x.availability[['sun','mon','tue','wed','thu','fri','sat'][new Date(sel.date + 'T12:00:00').getDay()]] === false ? ' (marked unavailable)' : ''}`}
                </option>
              ))}
            </select>
            <button onClick={() => reassign(false)} disabled={!target || saving}
                    className="w-full h-11 rounded-xl bg-[#FFD66B] text-[#1A1325] font-bold disabled:opacity-40 flex items-center justify-center gap-2"
                    data-testid="reassign-confirm">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Reassign on live schedule
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeekMirror;
